import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { UseQueryResult } from 'react-query';
import { Navigate, Outlet } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { pageNames, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { useGetWorker } from '../../../hooks/queries/worker';
import useToastHook from '../../../hooks/ToastHook';
import { Worker } from '../../../models/gen/worker';
import { Formatter } from '../../../utils/FormatUtils';
import Wireframe from '../../wireframe/Wireframe';

export type UserInfoContextType = {
  isLoading?: boolean;
  worker?: Worker;
  refetch: UseQueryResult['refetch'];
  workingDays?: number;
  isOutsourceMember?: boolean;
};

/**
 * 個人情報設定画面
 *
 * - URL: `/user-info/{mode}`
 *   - mode: edit / view [default]
 * - Parameter: なし
 * - Query: なし
 *
 * @returns
 */
const UserInfo: React.FunctionComponent = () => {
  const { state, actions } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  React.useEffect(() => {
    refetch();
    actions.updatePageInfo({ USER_INFO: { openedPanel: 0 } });

    return () => actions.updatePageInfo({ USER_INFO: undefined });
  }, []);

  const { toast } = useToastHook();
  const form = useForm<Worker>();
  const {
    data: workerGetResult,
    error,
    isFetched,
    isLoading,
    isError,
    refetch,
  } = useGetWorker({ usercode: state.userInfo?.user_code });

  React.useEffect(() => {
    if (!isLoading && isFetched) {
      form.reset({
        ...Object.entries(workerGetResult?.result || {}).reduce(
          (prev, [curK, curV]) => ({
            ...prev,
            [curK]: TypeUtils.toStrictValue(curV),
          }),
          {}
        ),
        dateOfBirth: Formatter.date(workerGetResult?.result?.dateOfBirth),
        hiredDate: Formatter.date(workerGetResult?.result?.hiredDate),
        firstDateOfPayRaise: Formatter.date(workerGetResult?.result?.hiredDate),
        workerReference: {
          ...Object.entries(
            workerGetResult?.result?.workerReference || {}
          ).reduce(
            (prev, [curK, curV]) => ({
              ...prev,
              [curK]: TypeUtils.toStrictValue(curV),
            }),
            {}
          ),
          startDateOfExperience: Formatter.date(
            workerGetResult?.result?.workerReference?.startDateOfExperience
          ),
          lastHealthCheckDate: Formatter.date(
            workerGetResult?.result?.workerReference?.lastHealthCheckDate
          ),
          specialHealthCheckDate: Formatter.date(
            workerGetResult?.result?.workerReference?.specialHealthCheckDate
          ),
        },
      });
    }
  }, [isLoading, workerGetResult?.result]);

  React.useEffect(() => {
    if (isError) {
      toast(error instanceof AppError ? error : '職人情報の取得に失敗しました');
    }
  }, [isError]);

  return (
    <Wireframe isLoaded={!isLoading} title={pageNames.USER_INFO}>
      <FormProvider {...form}>
        <Outlet
          context={{
            worker: workerGetResult?.result,
            isLoading,
            refetch,
            workingDays: workerGetResult?.workingDays,
          }}
        />
      </FormProvider>
    </Wireframe>
  );
};

export default UserInfo;
